import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('SolutionsService');

const baseUrl = environment.services.account.baseUrl;
const accountsUrl = baseUrl + environment.services.account.basePath;
const solutionsBaseUrl = environment.services.solution.baseUrl;
const solutionsUrl = solutionsBaseUrl + environment.services.solution.basePath;

export interface Solution {
  id: number;
  name: string;
  // other properties that might be needed
}

export interface UserSolution {
  id: number;
  solution: {
    id: number;
    name: string;
  };
}

export interface SolutionsResponse {
  count: number;
  data: Solution[];
}

export interface UserSolutionsResponse {
  count: number;
  data: UserSolution[];
}

class SolutionsService {
  /**
   * Get all solutions available for an account
   */
  async getAccountSolutions(accountId: number): Promise<SolutionsResponse> {
    try {
      const apiUrl = `${solutionsUrl}/solutions/?accountId=${accountId}&sort=name&size=49999999`;
      log.log('Fetching solutions from API:', apiUrl);
      
      const response = await apiService.makeRequest('get', apiUrl);
      return response;
    } catch (error) {
      log.error('Error fetching solutions:', error);
      throw error;
    }
  }

  /**
   * Get all solutions assigned to a user
   */
  async getUserSolutions(userId: number, accountId: number): Promise<UserSolutionsResponse> {
    try {
      const apiUrl = `${accountsUrl}/users/${userId}/solutions/?sort=name&direction=asc&page=0&size=49999999&accountId=${accountId}&include=SOLUTION_NAME`;
      log.log('Fetching user solutions from API:', apiUrl);
      
      const response = await apiService.makeRequest('get', apiUrl);
      return response;
    } catch (error) {
      log.error(`Error fetching solutions for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Add a solution to a user
   */
  async addSolutionToUser(userId: number, solutionId: number): Promise<any> {
    try {
      const apiUrl = `${accountsUrl}/users/${userId}/solutions/${solutionId}`;
      log.log('Adding solution to user:', apiUrl);
      
      const payload = {
        userId,
        solutionId
      };
      
      const response = await apiService.makeRequest('put', apiUrl, payload);
      
      // Invalidate cache for users list and this specific user
      apiService.invalidateCacheByPattern(`/users/${userId}/solutions`);
      
      return response;
    } catch (error) {
      log.error(`Error adding solution ${solutionId} to user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Remove a solution from a user
   */
  async removeSolutionFromUser(userId: number, solutionId: number): Promise<any> {
    try {
      const apiUrl = `${accountsUrl}/users/${userId}/solutions/${solutionId}`;
      log.log('Removing solution from user:', apiUrl);
      
      const response = await apiService.makeRequest('delete', apiUrl);
      
      // Invalidate cache for users list and this specific user
      apiService.invalidateCacheByPattern(`/users/${userId}/solutions`);
      
      return response;
    } catch (error) {
      log.error(`Error removing solution ${solutionId} from user ${userId}:`, error);
      throw error;
    }
  }
}

export const solutionsService = new SolutionsService();
export default solutionsService;